import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { HiMenuAlt3 } from "react-icons/hi";
import { IoIosArrowDown } from "react-icons/io";
import { motion, AnimatePresence } from "framer-motion";
import logo from "../../Assets/Logos/Logo.svg";
import TopNav from "./TopNav";

const BottomNavbar = () => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const navigate = useNavigate();
  const menuRef = useRef(null);
  const desktopDropdownRef = useRef(null);
  const desktopTriggerRef = useRef(null);

  const menuItems = [
    { title: "ABOUT", path: "/about" },
    { title: "RENTALS", path: "/rental" },
    { title: "MEMBERSHIPS", path: "/membership" },
    { title: "ACADEMIES", path: "/sports-academy" },
    // { title: "EVENTS", path: "/events" },
    { title: "LEAGUES", path: "/leagues" },
    { title: "EAT & DRINK", path: "/bar-and-restaurant" },
    { title: "BOOK NOW", path: "/our-bookings" },
    { title: "PARTIES & EVENTS", path: "/event-packages" },
    { title: "CONTACT", path: "/contact" },
    {
      title: "CAREERS",
      options: [
        { title: "COACHING", path: "/careers/coaching" },
        { title: "KITCHEN", path: "/careers/kitchen" },
        { title: "BAR", path: "/careers/bar" },
        { title: "OTHERS", path: "/careers/others" },
      ],
    },
    { title: "FAQ", path: "/faqs" },
  ];

  // Close sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setMenuOpen(false);
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Close desktop dropdown when clicking outside
  useEffect(() => {
    const handleClickOutsideDropdown = (e) => {
      if (
        activeDropdown !== null &&
        desktopDropdownRef.current &&
        !desktopDropdownRef.current.contains(e.target) &&
        desktopTriggerRef.current &&
        !desktopTriggerRef.current.contains(e.target)
      ) {
        setActiveDropdown(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideDropdown);
    return () =>
      document.removeEventListener("mousedown", handleClickOutsideDropdown);
  }, [activeDropdown]);

  const handleNavigate = (path) => {
    navigate(path);
    setMenuOpen(false);
    setActiveDropdown(null);
    window.scrollTo(0, 0);
  };

  // Toggle dropdown in sidebar
  const toggleSidebarDropdown = (index, e) => {
    e.stopPropagation(); // Prevent event bubbling
    setActiveDropdown(activeDropdown === index ? null : index);
  };

  return (
    <div className="w-full">
      <TopNav />
      <div className="w-full z-50 bg-[#141417] shadow-md font-heading capitalize">
        {/* BottomNavbar */}
        <div className="flex items-center justify-between px-4 md:px-8 lg:px-12 py-2">
          {/* Left side - Logo + Menu */}
          <div className="flex items-center gap-8">
            {/* Logo */}
            <img
              src={logo}
              alt="Logo"
              className="h-12 md:h-16 cursor-pointer"
              onClick={() => handleNavigate("/")}
            />

            {/* Menu Items (Desktop Only) */}
            <div className="hidden xl:flex gap-4 items-center text-sm tracking-wider">
              {menuItems.map((item, index) => (
                <div key={index} className="relative">
                  {item.options ? (
                    <>
                      <div
                        ref={
                          activeDropdown === index ? desktopTriggerRef : null
                        }
                        className="flex items-center gap-1 cursor-pointer text-white hover:text-primaryColor transition"
                        onClick={() =>
                          setActiveDropdown(
                            activeDropdown === index ? null : index,
                          )
                        }
                      >
                        {item.title} <IoIosArrowDown size={16} />
                      </div>
                      {activeDropdown === index && (
                        <div
                          ref={desktopDropdownRef}
                          className="absolute top-full left-0 mt-2 bg-[#1f1f24] shadow-lg rounded-md py-2 min-w-[180px] z-50 border border-gray-800"
                        >
                          {item.options.map((option, optIndex) => (
                            <button
                              key={optIndex}
                              onClick={() => handleNavigate(option.path)}
                              className="block w-full text-left px-4 py-2 text-sm text-white hover:bg-primaryBtnHoverColor hover:text-white transition capitalize"
                            >
                              {option.title}
                            </button>
                          ))}
                        </div>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={() => handleNavigate(item.path)}
                      className="text-white hover:text-primaryBtnHoverColor duration-150 transition capitalize"
                    >
                      {item.title}
                    </button>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Right side - Menu Icon */}
          <div className="flex items-center gap-8">
            <HiMenuAlt3
              size={28}
              className="text-white cursor-pointer"
              onClick={() => setMenuOpen(true)}
            />
          </div>
        </div>

        {/* Sidebar Menu */}
        <AnimatePresence>
          {menuOpen && (
            <>
              {/* Background Overlay */}
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.9 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.3 }}
                className="fixed inset-0 bg-[#141417] z-40"
                onClick={() => setMenuOpen(false)}
              />

              {/* Sidebar */}
              <motion.div
                ref={menuRef}
                initial={{ x: "100%" }}
                animate={{ x: 0 }}
                exit={{ x: "100%" }}
                transition={{ duration: 0.3 }}
                className="fixed top-0 right-0 w-3/4 sm:w-1/2 md:w-1/4 h-full bg-white shadow-lg z-50 p-6 flex flex-col gap-6 overflow-y-auto"
              >
                {/* Sidebar Header */}
                <div className="flex justify-between items-center border-b pb-4">
                  <div className="text-xl font-bold text-primaryBtnHoverColor">
                    Menu
                  </div>
                  <button
                    className="bg-gray-200 text-gray-700 px-3 py-1 rounded-md"
                    onClick={() => setMenuOpen(false)}
                  >
                    Close
                  </button>
                </div>

                {/* Sidebar Items */}
                {menuItems.map((item, index) => (
                  <div key={index}>
                    {item.options ? (
                      <div>
                        <div
                          className="flex items-center cursor-pointer justify-between text-primaryTextColor font-semibold"
                          onClick={(e) => toggleSidebarDropdown(index, e)}
                        >
                          {item.title}
                          <IoIosArrowDown
                            className={`transition-transform duration-200 ${
                              activeDropdown === index ? "rotate-180" : ""
                            }`}
                          />
                        </div>
                        {activeDropdown === index && (
                          <div className="ml-4 mt-2 flex flex-col gap-2 border-l pl-3">
                            {item.options.map((option, optIndex) => (
                              <div
                                key={optIndex}
                                className="text-primaryTextColor cursor-pointer font-medium text-sm hover:text-primaryBtnHoverColor"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleNavigate(option.path);
                                }}
                              >
                                {option.title}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div
                        className="text-primaryTextColor cursor-pointer font-semibold"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleNavigate(item.path);
                        }}
                      >
                        {item.title}
                      </div>
                    )}
                  </div>
                ))}
              </motion.div>
            </>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

export default BottomNavbar;

// import React, { useEffect, useRef, useState } from "react";
// import { useNavigate } from "react-router-dom";
// import { HiMenuAlt3 } from "react-icons/hi";
// import { IoIosArrowDown } from "react-icons/io";
// import { motion, AnimatePresence } from "framer-motion";
// import logo from "../../Assets/Logos/Logo.svg";
// import TopNav from "./TopNav";

// const BottomNavbar = () => {
//   const [menuOpen, setMenuOpen] = useState(false);
//   const [activeDropdown, setActiveDropdown] = useState(null);
//   const navigate = useNavigate();
//   const menuRef = useRef(null);

//   const menuItems = [
//     { title: "ABOUT", path: "/about" },
//     { title: "RENTALS", path: "/rental" },
//     { title: "MEMBERSHIPS", path: "/membership" },
//     { title: "ACADEMIES", path: "/sports-academy" },
//     // { title: "EVENTS", path: "/events" },
//     { title: "LEAGUES", path: "/leagues" },
//     { title: "BAR & RESTAURANT", path: "/bar-and-restaurant" },
//     { title: "BOOK NOW", path: "/our-bookings" },
//     { title: "CONTACT", path: "/contact" },
//     { title: "CAREERS", path: "/careers" },

//     { title: "FAQS", path: "/faqs" },
//   ];

//   useEffect(() => {
//     const handleClickOutside = (e) => {
//       if (menuRef.current && !menuRef.current.contains(e.target)) {
//         setMenuOpen(false);
//         setActiveDropdown(null);
//       }
//     };
//     document.addEventListener("mousedown", handleClickOutside);
//     return () => document.removeEventListener("mousedown", handleClickOutside);
//   }, []);

//   const handleNavigate = (path) => {
//     navigate(path);
//     setMenuOpen(false);
//     setActiveDropdown(null);
//     window.scrollTo(0, 0);
//   };

//   return (
//     <div className="w-full">
//       <TopNav />
//       <div className="w-full z-50 bg-[#141417] shadow-md font-heading capitalize">
//         {/* BottomNavbar */}
//         <div className="flex items-center justify-between px-4 md:px-8 lg:px-12 py-2">
//           {/* Left side - Logo + Menu */}
//           <div className="flex items-center gap-8">
//             {/* Logo */}
//             <img
//               src={logo}
//               alt="Logo"
//               className="h-12 md:h-16 cursor-pointer"
//               onClick={() => handleNavigate("/")}
//             />

//             {/* Menu Items (Desktop Only) */}
//             <div className="hidden xl:flex gap-4 items-center text-sm tracking-wider">
//               {menuItems.map((item, index) => (
//                 <div key={index} className="relative">
//                   {item.options ? (
//                     <div
//                       className="flex items-center gap-1 cursor-pointer text-white hover:text-primaryColor transition"
//                       onClick={() =>
//                         setActiveDropdown(
//                           activeDropdown === index ? null : index
//                         )
//                       }
//                     >
//                       {item.title} <IoIosArrowDown size={16} />
//                     </div>
//                   ) : (
//                     <button
//                       onClick={() => handleNavigate(item.path)}
//                       className="text-white hover:text-primaryBtnHoverColor duration-150 transition capitalize"
//                     >
//                       {item.title}
//                     </button>
//                   )}
//                 </div>
//               ))}
//             </div>
//           </div>

//           {/* Right side - Login + Menu Icon */}
//           <div className="flex items-center gap-8">
//             {/* Hamburger (Always Visible) */}
//             <HiMenuAlt3
//               size={28}
//               className="text-white cursor-pointer"
//               onClick={() => setMenuOpen(true)}
//             />
//           </div>
//         </div>

//         {/* Sidebar Menu */}
//         <AnimatePresence>
//           {menuOpen && (
//             <>
//               {/* Background Overlay */}
//               <motion.div
//                 initial={{ opacity: 0 }}
//                 animate={{ opacity: 0.9 }}
//                 exit={{ opacity: 0 }}
//                 transition={{ duration: 0.3 }}
//                 className="fixed inset-0 bg-[#141417] z-40"
//                 onClick={() => setMenuOpen(false)}
//               />

//               {/* Sidebar */}
//               <motion.div
//                 ref={menuRef}
//                 initial={{ x: "100%" }}
//                 animate={{ x: 0 }}
//                 exit={{ x: "100%" }}
//                 transition={{ duration: 0.3 }}
//                 className="fixed top-0 right-0 w-3/4 sm:w-1/2 md:w-1/4 h-full bg-white shadow-lg z-50 p-6 flex flex-col gap-6"
//               >
//                 {/* Sidebar Header */}
//                 <div className="flex justify-between items-center border-b pb-4">
//                   <div className="text-xl font-bold text-primaryBtnHoverColor">
//                     Menu
//                   </div>
//                   <button
//                     className="bg-gray-200 text-gray-700 px-3 py-1 rounded-md"
//                     onClick={() => setMenuOpen(false)}
//                   >
//                     Close
//                   </button>
//                 </div>

//                 {/* Sidebar Items */}
//                 {menuItems.map((item, index) => (
//                   <div key={index}>
//                     {item.options ? (
//                       <div>
//                         <div
//                           className="flex items-center cursor-pointer justify-between text-primaryTextColor font-semibold"
//                           onClick={() =>
//                             setActiveDropdown(
//                               activeDropdown === index ? null : index
//                             )
//                           }
//                         >
//                           {item.title}
//                           <IoIosArrowDown />
//                         </div>
//                       </div>
//                     ) : (
//                       <div
//                         className="text-primaryTextColor cursor-pointer font-semibold"
//                         onClick={() => handleNavigate(item.path)}
//                       >
//                         {item.title}
//                       </div>
//                     )}
//                   </div>
//                 ))}
//               </motion.div>
//             </>
//           )}
//         </AnimatePresence>
//       </div>
//     </div>
//   );
// };

// export default BottomNavbar;
